import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ProBadge } from "@/components/ui/ProBadge";
import { SegmentedControl } from "@/components/ui/visual-controls/SegmentedControl";
import {
  isBackgroundPro,
  isAnimationPro,
} from "../design-tokens";
import type { BackgroundStyle, AnimationLevel } from "@profolio/validators";
import { Sparkles, Activity, Layers, Check } from "lucide-react";

interface EffectsSubViewProps {
  currentDesign: any;
  updateTransientPreview: (updates: any) => void;
  isPro: boolean;
  onUpgradeClick: (featureKey?: string) => void;
}

export function EffectsSubView({
  currentDesign,
  updateTransientPreview,
  isPro,
  onUpgradeClick,
}: EffectsSubViewProps) {
  const handleSelectBackground = (bg: BackgroundStyle) => {
    if (isBackgroundPro(bg) && !isPro) {
      onUpgradeClick("background_styles");
      return;
    }
    updateTransientPreview({ backgroundStyle: bg });
  };

  const handleSelectAnimation = (anim: AnimationLevel) => {
    if (isAnimationPro(anim) && !isPro) {
      onUpgradeClick("animations");
      return;
    }
    updateTransientPreview({ animationLevel: anim });
  };

  // Resolve current bevel state (supports both direct pageBevel and nested effects.pageBevel)
  const pageBevel =
    currentDesign.layoutOverrides?.pageBevel ||
    currentDesign.layoutOverrides?.effects?.pageBevel;
  const isBevelActive = Boolean(pageBevel && pageBevel !== "none");
  const currentBevelVal: "light" | "medium" | "high" =
    pageBevel === "high" || pageBevel === "mobile-bezel"
      ? "high"
      : pageBevel === "medium" || pageBevel === "curved-glass"
      ? "medium"
      : "light";

  const currentColor =
    currentDesign.layoutOverrides?.pageBevelColor ||
    currentDesign.layoutOverrides?.effects?.pageBevelColor ||
    "white";

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="border-b border-border pb-3">
        <h3 className="text-base font-bold text-foreground">Atmosphere & Visual Effects</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Fine-tune 2.5D viewport edge refraction curves, canvas background textures, and dynamic motion transitions.
        </p>
      </div>

      {/* ── 1. Screen Edge Curve & 2.5D Bevel Glass (Primary Feature) ── */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Screen Edge Curve & Reflection (2.5D Edge Bevel)
          </Label>
        </div>

        <div className="p-4 sm:p-5 rounded-2xl border border-border bg-card shadow-xs space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5 max-w-lg">
              <div className="text-sm font-semibold text-foreground flex items-center gap-2">
                <span>Curved Glass Edge Refraction</span>
                {isBevelActive && (
                  <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                    Active
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground leading-relaxed">
                Renders a physical curved-glass edge highlight along both vertical viewport borders, creating tactile OLED screen depth.
              </div>
            </div>
            <Switch
              checked={isBevelActive}
              onCheckedChange={(checked) => {
                const currentLayout = currentDesign.layoutOverrides || {};
                const bevelVal = checked ? "medium" : "none";
                updateTransientPreview({
                  layoutOverrides: {
                    ...currentLayout,
                    pageBevel: bevelVal,
                    ...(currentLayout.effects
                      ? { effects: { ...currentLayout.effects, pageBevel: bevelVal } }
                      : {}),
                  },
                });
              }}
            />
          </div>

          {isBevelActive && (
            <div className="pt-4 border-t border-border space-y-5 animate-in fade-in duration-200">
              {/* Edge Curve Intensity Tier */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-foreground">Edge Curve Radius / Intensity</Label>
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {currentBevelVal === "light"
                      ? "12px Waterfall Rim"
                      : currentBevelVal === "medium"
                      ? "18px Curved Display"
                      : "26px Deep Bezel"}
                  </span>
                </div>
                <SegmentedControl
                  value={currentBevelVal}
                  onChange={(val) => {
                    const currentLayout = currentDesign.layoutOverrides || {};
                    const tierId = val as "light" | "medium" | "high";
                    updateTransientPreview({
                      layoutOverrides: {
                        ...currentLayout,
                        pageBevel: tierId,
                        ...(currentLayout.effects
                          ? { effects: { ...currentLayout.effects, pageBevel: tierId } }
                          : {}),
                      },
                    });
                  }}
                  options={[
                    { value: "light", label: "Subtle (12px)", tooltip: "Tight waterfall rim" },
                    { value: "medium", label: "Balanced (18px)", tooltip: "Curved display reflection" },
                    { value: "high", label: "Pronounced (26px)", tooltip: "Deeper glass edge" },
                  ]}
                />
              </div>

              {/* Specular Highlight Tint */}
              <div className="space-y-2.5">
                <Label className="text-xs font-semibold text-foreground">Specular Reflection Tint</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: "white", label: "Pure Crystal", colorPreview: "bg-white border-slate-300 dark:border-slate-600", desc: "Clean neutral glass sheen" },
                    { id: "black", label: "Smoked Obsidian", colorPreview: "bg-slate-900 border-slate-700", desc: "Dark ambient vignette" },
                    { id: "accent", label: "Theme Accent", colorPreview: "bg-primary border-primary/50", desc: "Adaptive to palette accent" },
                    { id: "cyan", label: "Ice Blue", colorPreview: "bg-cyan-400 border-cyan-300 dark:border-cyan-600", desc: "Cybernetic OLED glow" },
                    { id: "gold", label: "Champagne Amber", colorPreview: "bg-amber-400 border-amber-300 dark:border-amber-600", desc: "Warm luxury sheen" },
                    { id: "purple", label: "Neon Violet", colorPreview: "bg-purple-500 border-purple-300 dark:border-purple-600", desc: "Atmospheric evening tint" },
                  ].map((colorOption) => {
                    const isSelected = currentColor === colorOption.id;

                    return (
                      <button
                        key={colorOption.id}
                        type="button"
                        onClick={() => {
                          const currentLayout = currentDesign.layoutOverrides || {};
                          const colorId = colorOption.id as "white" | "black" | "accent" | "cyan" | "gold" | "purple";
                          updateTransientPreview({
                            layoutOverrides: {
                              ...currentLayout,
                              pageBevelColor: colorId,
                              ...(currentLayout.effects
                                ? { effects: { ...currentLayout.effects, pageBevelColor: colorId } }
                                : {}),
                            },
                          });
                        }}
                        className={`h-11 px-3 rounded-xl border text-xs font-medium flex items-center justify-between transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                          isSelected
                            ? "border-primary ring-1 ring-primary bg-primary/10 shadow-2xs font-semibold"
                            : "border-border bg-card/60 hover:bg-muted/40 hover:border-border/80"
                        }`}
                        title={`${colorOption.label} — ${colorOption.desc}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`w-3.5 h-3.5 rounded-full ${colorOption.colorPreview} border shadow-2xs shrink-0`} />
                          <span className="truncate text-foreground">{colorOption.label}</span>
                        </div>
                        {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0 ml-1" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Canvas Background Style ── */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Layers className="h-4 w-4 text-primary" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Canvas Background Atmosphere
            </Label>
          </div>
          {!isPro && <ProBadge locked />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[
            {
              id: "solid",
              label: "Solid Color",
              desc: "Clean, flat minimalist background",
              previewClass: "bg-background",
            },
            {
              id: "subtle-gradient",
              label: "Subtle Gradient",
              desc: "Gentle vertical ambient gradient",
              previewClass: "bg-gradient-to-b from-primary/15 via-background to-background",
            },
            {
              id: "mesh",
              label: "Mesh Aura",
              desc: "Dynamic multi-point atmospheric blur",
              previewClass: "bg-[radial-gradient(circle_at_top_left,rgba(99,102,241,0.2),transparent_50%),radial-gradient(circle_at_bottom_right,rgba(236,72,153,0.15),transparent_50%)] bg-background",
            },
            {
              id: "animated-gradient",
              label: "Animated Glow",
              desc: "Flowing, living background aura",
              previewClass: "bg-gradient-to-tr from-primary/20 via-background to-amber-500/15 animate-pulse",
            },
            {
              id: "dot-grid",
              label: "Dot Matrix",
              desc: "Subtle technical dot pattern",
              previewClass: "bg-[radial-gradient(#888_1px,transparent_1px)] [background-size:12px_12px] bg-background",
            },
          ].map((bg) => {
            const isSelected = currentDesign.backgroundStyle === bg.id;
            const locked = isBackgroundPro(bg.id as BackgroundStyle) && !isPro;

            return (
              <button
                key={bg.id}
                type="button"
                onClick={() => handleSelectBackground(bg.id as BackgroundStyle)}
                className={`group relative p-3.5 rounded-xl border text-left transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? "border-primary ring-1 ring-primary bg-primary/5 shadow-xs"
                    : "border-border bg-card hover:border-primary/40 hover:bg-muted/30"
                }`}
              >
                {/* Mini preview bar */}
                <div className={`h-8 w-full rounded-md border border-border/60 mb-2.5 overflow-hidden ${bg.previewClass}`} />
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-foreground">{bg.label}</span>
                  {locked ? <ProBadge locked /> : isSelected ? <Check className="h-3 w-3 text-primary" /> : null}
                </div>
                <p className="text-[11px] text-muted-foreground line-clamp-1">{bg.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── 3. Animation Dynamics ── */}
      <div className="space-y-4 border-t border-border pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Motion Dynamics & Animations
            </Label>
          </div>
          {!isPro && <ProBadge locked />}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { id: "none", label: "Static / None", desc: "No animations (High performance & WCAG AAA accessible)" },
            { id: "subtle", label: "Subtle Motion", desc: "Gentle opacity fade-ins on scroll & load" },
            { id: "smooth", label: "Smooth Transitions", desc: "Fluid cubic-bezier ease on cards and buttons" },
            { id: "spring", label: "Spring Physics", desc: "Playful bouncy micro-interactions on hover" },
            { id: "snappy", label: "Snappy Feedback", desc: "Rapid responsive tactile clicks and tabs" },
          ].map((anim) => {
            const isSelected = (currentDesign.animationLevel || "subtle") === anim.id;
            const locked = isAnimationPro(anim.id as AnimationLevel) && !isPro;

            return (
              <button
                key={anim.id}
                type="button"
                onClick={() => handleSelectAnimation(anim.id as AnimationLevel)}
                className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isSelected
                    ? "border-primary ring-1 ring-primary bg-primary/5 shadow-xs"
                    : "border-border bg-card hover:border-primary/40 hover:bg-muted/30"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-foreground">{anim.label}</span>
                  {locked ? <ProBadge locked /> : isSelected ? <Check className="h-3 w-3 text-primary" /> : null}
                </div>
                <p className="text-[11px] text-muted-foreground">{anim.desc}</p>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
